import { ChangeRequest } from '@app/core/model/change-request.model'
import { ScopeConfiguration, ScopeVersion } from '@app/core/model/scope-version'
import { createSelector } from '@ngrx/store'
import { ChangeRequestLoadingState, ChangeRequestState } from '../../model/change-request.state.model'
import { getScopeOverviewState, State } from '@app/features/scope-overview/store/reducers'
import { ChangeItemAdapter } from '../adapters/change-item.adapter'
import { ChangeItem } from '../../model/change-item.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { Dictionary, groupBy, keyBy } from 'lodash'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { mapChangeRequestSections } from '../../util'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'

const getFeatureState = createSelector(getScopeOverviewState, (state: State): ChangeRequestState => state.changeRequest)

// Loading State Selectors
const selectChangeRequestLoadingState = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ChangeRequestLoadingState => state?.loading
)

export const selectChangeRequestLoading = createSelector(
  selectChangeRequestLoadingState,
  (loadingState: ChangeRequestLoadingState): boolean => loadingState?.changeRequest
)

export const selectPreviewScopeLoading = createSelector(
  selectChangeRequestLoadingState,
  (loadingState: ChangeRequestLoadingState): boolean => loadingState?.previewScope
)

// Change Items Entity Selectors
const changeItemsAdapterSelectors = ChangeItemAdapter.getSelectors()

const selectChangeItemsState = createSelector(getFeatureState, (state: ChangeRequestState) => state?.changeItems)

export const selectAllChangeItems = createSelector(selectChangeItemsState, changeItemsAdapterSelectors.selectAll)

export const selectChangeItemEntities = createSelector(
  selectChangeItemsState,
  changeItemsAdapterSelectors.selectEntities
)

export const selectChangeItemByIdentity = (identity: number | string) =>
  createSelector(selectChangeItemEntities, (entities): ChangeItem | undefined => entities[identity])

// Change Request State Selectors
export const selectCurrentChangeRequest = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ChangeRequest | undefined => state?.currentChangeRequest
)

export const changeRequestAbleToMerge = createSelector(
  getFeatureState,
  (state: ChangeRequestState): boolean | undefined => state?.ableToMerge
)

export const selectChangeRequests = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ChangeRequest[] => state?.changeRequests
)
export const selectChangeRequestsMap = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Dictionary<ChangeRequest> => keyBy(state?.changeRequests, 'id')
)

export const selectPreviewScope = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ScopeVersion | undefined => state?.previewScope
)

export const selectPreviewConfiguration = createSelector(
  selectPreviewScope,
  (previewScope: ScopeVersion): ScopeConfiguration => previewScope?.configuration || {}
)

export const selectPreviewDeliverables = createSelector(
  selectPreviewScope,
  (previewScope: ScopeVersion): Deliverable[] => previewScope?.deliverables || []
)

export const selectPreviewDeliverablesMap = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Dictionary<Deliverable> => state?.previewDeliverablesMap
)

// Mapped Deliverables Selectors
export const selectMappedDeliverables = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Deliverable[] => state?.mappedDeliverables
)
export const selectMappedUnsectionedDeliverables = createSelector(
  selectMappedDeliverables,
  (deliverables: Deliverable[]): Deliverable[] => filterUnsectionedDeliverables(deliverables)
)

export const selectMappedTrackedDeliverables = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Deliverable[] => state?.mappedTrackedDeliverables
)
export const selectMappedUnsectionedTrackedDeliverables = createSelector(
  selectMappedTrackedDeliverables,
  (deliverables: Deliverable[]): Deliverable[] => filterUnsectionedDeliverables(deliverables)
)

export const selectMappedDeliverablesBySection = createSelector(
  selectMappedDeliverables,
  (deliverables: Deliverable[]): Dictionary<Deliverable[]> => groupBySection(deliverables)
)
export const selectMappedTrackedDeliverablesBySection = createSelector(
  selectMappedTrackedDeliverables,
  (deliverables: Deliverable[]): Dictionary<Deliverable[]> => groupBySection(deliverables)
)

export const selectMappedDeliverableSections = (isTracked: boolean) =>
  createSelector(
    ScopeOverviewSelectors.selectCurrentScope,
    isTracked ? selectMappedTrackedDeliverablesBySection : selectMappedDeliverablesBySection,
    (scope: ScopeVersion, deliverablesBySection: Dictionary<Deliverable[]>): ScopeSection[] =>
      mapChangeRequestSections(scope, deliverablesBySection)
  )

export const ChangeRequestSelectors = {
  selectChangeRequestLoading,
  selectPreviewScopeLoading,

  changeRequestAbleToMerge,
  getFeatureState,
  selectCurrentChangeRequest,
  selectChangeRequests,
  selectChangeRequestsMap,
  selectAllChangeItems,
  selectChangeItemEntities,
  selectPreviewScope,
  selectPreviewConfiguration,
  selectPreviewDeliverables,
  selectPreviewDeliverablesMap,

  selectMappedUnsectionedDeliverables,
  selectMappedUnsectionedTrackedDeliverables,
  selectMappedDeliverableSections,
}

const groupBySection = (deliverables: Deliverable[]): Dictionary<Deliverable[]> => groupBy(deliverables, 'section.id')
const filterUnsectionedDeliverables = (deliverables: Deliverable[]): Deliverable[] =>
  deliverables.filter((deliverable) => !deliverable.section)
